// Name and postal address — who support is talking to, and where an invoice
// or a shipping label would go. See the 20260921000700 migration.
//
// The Flutter mirror is app/mobile-app/lib/src/screens/personal_details_card.dart.

import { useEffect, useState } from "react";
import { COUNTRY_ZONES } from "../lib/zones.generated";
import { EMPTY_PROFILE, loadProfile, saveProfile, type Profile } from "../lib/profile";
import { listMyWorkspaces, type WorkspaceSummary } from "../lib/workspace";
import { logError } from "../lib/logger";
import { Spinner } from "./Spinner";

export function PersonalDetailsCard() {
  const [saved, setSaved] = useState<Profile>(EMPTY_PROFILE);
  const [draft, setDraft] = useState<Profile>(EMPTY_PROFILE);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[] | null>(null);

  useEffect(() => {
    let live = true;
    loadProfile()
      .then((p) => { if (live) { setSaved(p); setDraft(p); } })
      .catch((e) => {
        setError(e.message ?? String(e));
        void logError("app", e, { step: "load_profile" });
      })
      .finally(() => { if (live) setReady(true); });
    // Only needed to populate the WhatsApp workspace picker; a failure here
    // must not block the rest of the card, so it's caught separately.
    listMyWorkspaces()
      .then((list) => { if (live) setWorkspaces(list); })
      .catch((e) => { void logError("app", e, { step: "load_workspaces_for_whatsapp" }); });
    return () => { live = false; };
  }, []);

  function set<K extends keyof Profile>(key: K, value: string) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  const dirty = ready && (Object.keys(saved) as (keyof Profile)[])
    .some((k) => (draft[k] ?? "") !== (saved[k] ?? ""));

  async function save() {
    setBusy(true);
    setError(null);
    setJustSaved(false);
    try {
      const cleaned = await saveProfile(draft);
      setSaved(cleaned);
      setDraft(cleaned);
      setJustSaved(true);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "save_profile" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Name and address</h2>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        Who support is talking to, and where an invoice would go. Nothing here is shown to
        anyone else.
      </p>

      {error && <div className="err">{error}</div>}

      <div className="row" style={{ gap: ".75rem" }}>
        <div style={{ flex: 1, minWidth: "10rem" }}>
          <label htmlFor="pd-first-name">First name</label>
          <input id="pd-first-name" value={draft.first_name ?? ""} maxLength={100}
                 autoComplete="given-name" disabled={busy}
                 onChange={(e) => set("first_name", e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: "10rem" }}>
          <label htmlFor="pd-last-name">Last name</label>
          <input id="pd-last-name" value={draft.last_name ?? ""} maxLength={100}
                 autoComplete="family-name" disabled={busy}
                 onChange={(e) => set("last_name", e.target.value)} />
        </div>
      </div>

      <div style={{ marginTop: ".6rem", maxWidth: "28rem" }}>
        <label htmlFor="pd-whatsapp">WhatsApp number (optional)</label>
        <input id="pd-whatsapp" type="tel" inputMode="tel"
               value={draft.whatsapp_number ?? ""} maxLength={32}
               autoComplete="tel" placeholder="+14254398054" disabled={busy}
               aria-describedby="pd-whatsapp-help"
               onChange={(e) => set("whatsapp_number", e.target.value)} />
        <p id="pd-whatsapp-help" className="muted">
          Include your country code. Saving this number does not verify it.
        </p>
      </div>

      {workspaces && workspaces.length > 1 && (
        <div style={{ marginTop: ".6rem", maxWidth: "28rem" }}>
          <label htmlFor="pd-whatsapp-workspace">WhatsApp posts for</label>
          <select id="pd-whatsapp-workspace" disabled={busy}
                  value={draft.whatsapp_workspace_id ?? ""}
                  onChange={(e) => set("whatsapp_workspace_id", e.target.value)}>
            <option value="">Choose automatically (only works with one workspace)</option>
            {workspaces.map((w) => (
              <option key={w.workspace_id} value={w.workspace_id}>{w.name}</option>
            ))}
          </select>
          <p className="muted">
            You belong to more than one workspace. Choose which one a WhatsApp photo posts to;
            without a choice here, automatic WhatsApp posting is refused rather than guessed.
          </p>
        </div>
      )}

      {draft.whatsapp_number && (
        <div style={{ marginTop: ".6rem", maxWidth: "28rem" }}>
          <label>
            <input type="checkbox" disabled={busy}
                   checked={draft.whatsapp_skip_review}
                   onChange={(e) => setDraft((d) => ({ ...d, whatsapp_skip_review: e.target.checked }))} />{" "}
            Publish my WhatsApp posts without review
          </label>
          <p className="muted">
            Off by default: you get a link to approve each post first. Turning this on publishes
            straight away, unless your workspace requires an approver.
          </p>
        </div>
      )}

      <div style={{ marginTop: ".6rem" }}>
        <label htmlFor="pd-address1">Address line 1</label>
        <input id="pd-address1" value={draft.address_line1 ?? ""} maxLength={200}
               autoComplete="address-line1" disabled={busy}
               onChange={(e) => set("address_line1", e.target.value)} />
      </div>

      <div style={{ marginTop: ".6rem" }}>
        <label htmlFor="pd-address2">Address line 2 (optional)</label>
        <input id="pd-address2" value={draft.address_line2 ?? ""} maxLength={200}
               autoComplete="address-line2" disabled={busy}
               onChange={(e) => set("address_line2", e.target.value)} />
      </div>

      <div className="row" style={{ marginTop: ".6rem", gap: ".75rem" }}>
        <div style={{ flex: 1, minWidth: "10rem" }}>
          <label htmlFor="pd-city">City</label>
          <input id="pd-city" value={draft.city ?? ""} maxLength={100}
                 autoComplete="address-level2" disabled={busy}
                 onChange={(e) => set("city", e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: "10rem" }}>
          <label htmlFor="pd-state">State / region</label>
          <input id="pd-state" value={draft.state ?? ""} maxLength={100}
                 autoComplete="address-level1" disabled={busy}
                 onChange={(e) => set("state", e.target.value)} />
        </div>
      </div>

      <div className="row" style={{ marginTop: ".6rem", gap: ".75rem" }}>
        <div style={{ flex: 1, minWidth: "8rem" }}>
          <label htmlFor="pd-postal">Postal code</label>
          <input id="pd-postal" value={draft.postal_code ?? ""} maxLength={20}
                 autoComplete="postal-code" disabled={busy}
                 onChange={(e) => set("postal_code", e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: "10rem" }}>
          <label htmlFor="pd-country">Country</label>
          <select id="pd-country" value={draft.country ?? ""} disabled={busy}
                  onChange={(e) => set("country", e.target.value)}>
            <option value="">Not set</option>
            {COUNTRY_ZONES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="row" style={{ marginTop: ".8rem" }}>
        <button className="primary sm" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? <><Spinner /> Saving…</> : "Save"}
        </button>
        {justSaved && !dirty && <span className="muted">Saved.</span>}
      </div>
    </div>
  );
}
